// i18n-ns: budget (row labels are resolved through the caller's t, not a local useTranslation)
import {
    BudgetScenarioConsolidatedMonth,
    BudgetScenarioConsolidatedPerScenario,
    BudgetScenarioConsolidatedResult,
} from '@callmart/types';

// The German board splits the Konsolidierte Ansicht into four stacked tables rather than one long
// grid: the untouched baseline (Ist Budget), the two adjustment blocks the selected scenarios feed
// (Anpassung Budget / Anpassung Kosten), and the resulting figures (Delta neu). Building the rows here
// — away from ag-grid — keeps the arithmetic and the labelling unit-testable.

export interface ConsolidatedBoardRow {
    // Stable per board, so ag-grid can key rows without re-creating them on every refetch.
    rowKey: string;
    label: string;
    // Month key → value. Months absent from the result render empty rather than 0.
    values: Record<string, number>;
    sum: number;
    // Indented, non-bold: a component of the line above rather than a figure of its own.
    isSub?: boolean;
    // Red/green by sign (only the delta lines).
    isDelta?: boolean;
    // A named scenario contribution, not a computed board line.
    isScenario?: boolean;
    // The scenario belongs to another version of the bucket — a legitimate contributor, but not part
    // of THIS version's own breakdown.
    isForeign?: boolean;
}

export interface ConsolidatedBoard {
    // i18n key of the card title (budget namespace).
    titleKey: string;
    rows: ConsolidatedBoardRow[];
    // Boards whose content is entirely the selection: worth an explicit empty hint when nothing is
    // selected, instead of a lone zero row that looks like a computed result.
    isAdjustmentBoard?: boolean;
}

type Translate = (key: string) => string;

// One board line, summed across the visible months.
const fixedRow = (
    key: keyof BudgetScenarioConsolidatedMonth,
    labelKey: string,
    months: string[],
    byMonth: Map<string, BudgetScenarioConsolidatedMonth>,
    t: Translate,
    extra?: Pick<ConsolidatedBoardRow, 'isSub' | 'isDelta'>
): ConsolidatedBoardRow => {
    const values: Record<string, number> = {};
    let sum = 0;
    for (const month of months) {
        const value = Number(byMonth.get(month)?.[key] ?? 0);
        values[month] = value;
        sum += value;
    }
    return { rowKey: String(key), label: t(labelKey), values, sum, ...extra };
};

// A delta line the DTO doesn't carry (the baseline's own Budget − Kosten gesamt). Derived from two
// published rows rather than added as an API field, so it can never disagree with them.
const derivedDeltaRow = (
    rowKey: string,
    labelKey: string,
    months: string[],
    byMonth: Map<string, BudgetScenarioConsolidatedMonth>,
    t: Translate,
    compute: (month: BudgetScenarioConsolidatedMonth) => number
): ConsolidatedBoardRow => {
    const values: Record<string, number> = {};
    let sum = 0;
    for (const month of months) {
        const row = byMonth.get(month);
        const value = row ? compute(row) : 0;
        values[month] = value;
        sum += value;
    }
    return { rowKey, label: t(labelKey), values, sum, isDelta: true };
};

// One row per selected scenario. The label carries the descriptors the board's own tables imply from
// their position: the Art (a name alone doesn't say what kind of what-if produced the delta) and,
// when the scenario belongs to another version of the bucket, that fact — unmarked it would read as
// this baseline's own scenario.
const scenarioRow = (
    entry: BudgetScenarioConsolidatedPerScenario,
    baselineVersionId: number,
    months: string[],
    t: Translate
): ConsolidatedBoardRow => {
    const isForeign = entry.budgetVersionId !== baselineVersionId;
    const descriptors = [t(`scenarioType.${entry.type}`), ...(isForeign ? [t('consolidated.otherVersion')] : [])];
    const values: Record<string, number> = {};
    for (const month of months) {
        values[month] = Number(entry.monthlyTotals[month] ?? 0);
    }
    return {
        rowKey: `scenario-${entry.scenarioId}`,
        label: `${entry.name} · ${descriptors.join(' · ')}`,
        values,
        sum: entry.grandTotal,
        isScenario: true,
        isForeign,
    };
};

export const buildConsolidatedBoards = (
    result: BudgetScenarioConsolidatedResult | undefined,
    t: Translate
): ConsolidatedBoard[] => {
    if (!result) return [];
    const { months, versionId } = result;
    const byMonth = new Map(result.monthly.map((row) => [row.month, row]));
    const fixed = (
        key: keyof BudgetScenarioConsolidatedMonth,
        labelKey: string,
        extra?: Pick<ConsolidatedBoardRow, 'isSub' | 'isDelta'>
    ) => fixedRow(key, labelKey, months, byMonth, t, extra);
    const perSide = (side: 'budget' | 'cost') =>
        (result.perScenario ?? [])
            .filter((entry) => entry.side === side)
            .map((entry) => scenarioRow(entry, versionId, months, t));

    return [
        {
            titleKey: 'consolidated.istBudget',
            rows: [
                fixed('originalBudget', 'consolidated.baselineBudget'),
                fixed('originalCosts', 'consolidated.baselineCosts'),
                derivedDeltaRow('originalDelta', 'consolidated.baselineDelta', months, byMonth, t, (row) =>
                    Number((row.originalBudget - row.originalCosts).toFixed(2))
                ),
            ],
        },
        {
            titleKey: 'consolidated.budgetAdjustmentTable',
            isAdjustmentBoard: true,
            rows: [...perSide('budget'), fixed('budgetAdjustments', 'consolidated.sumBudgetAdjustment')],
        },
        {
            titleKey: 'consolidated.costAdjustmentTable',
            isAdjustmentBoard: true,
            rows: [...perSide('cost'), fixed('costAdjustments', 'consolidated.sumCostAdjustment')],
        },
        {
            titleKey: 'consolidated.deltaNewTable',
            rows: [
                fixed('newBudget', 'consolidated.newBudget'),
                fixed('newCosts', 'consolidated.newCosts'),
                // Same Chancen/Risiken line the Budget summary shows, so the two screens read alike.
                fixed('onTopRisks', 'summary.opportunitiesRisks', { isSub: true }),
                fixed('kostenInklEvents', 'consolidated.kostenInklEvents'),
                fixed('newDelta', 'consolidated.deltaNew', { isDelta: true }),
            ],
        },
    ];
};
